import React, { FC } from "react";
import { State } from "../../../redux/slices";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";

import { ThunkDispatch } from "@reduxjs/toolkit";

import { FluxStandardAction } from "flux-standard-action";
import { coordinatesGeocoder } from "../../map/mapEditCreateDeleteLocations/MapEditCreateLocationSideBar";
import MapboxGeocoder, { Result } from "@mapbox/mapbox-gl-geocoder";
import mapboxgl from "mapbox-gl";

import { useNavigate } from "react-router-dom";

import {
  fetchLocationMapboxCoordinatesEditLoc,
  updateLocation,
} from "../../../redux/actions/locations/editLocation";
import {
  LocationSelectedIs,
  SelectedLocation,
} from "../../../redux/slices/grainTracker/grainTrackerTypes";
import {
  setEditionValues,
  setValuesEditLocation,
} from "../../../redux/slices/grainTracker/grainTrackerEdition";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    selectEditLocationValues: (selectedLocation: SelectedLocation) => {
      dispatch(setValuesEditLocation(selectedLocation));
    },

    setSearchAddressEditLoc: (longitude: number, latitude: number) => {
      dispatch(fetchLocationMapboxCoordinatesEditLoc({ longitude, latitude }));
    },

    setEditionValues: (values: LocationSelectedIs) => {
      dispatch(setEditionValues(values));
    },

    updateLocation: (token: string, navigate: any) => {
      dispatch(updateLocation({ token, navigate }));
    },
  };
};

export const isLatitudeVerify = (num: number) =>
  isFinite(num) && Math.abs(num) <= 90;
export const isLongitudeVerify = (num: number) =>
  isFinite(num) && Math.abs(num) <= 180;

export const EditLocationForm: FC<any> = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();

  const { locationSelectedIs, selectedLocation } = useSelector(
    (state: State) => state.GrainWiseEditionReducer
  );

  const { locationSelectedDetails } = useSelector(
    (state: State) => state.SingleLocationReducer
  );

  const { user } = useSelector((state: State) => state.Authentication);
  const { latitude, longitude, name } = selectedLocation.values;

  const token = user?.token ?? "";

  const {
    setSearchAddressEditLoc,
    selectEditLocationValues,
    setEditionValues,
    updateLocation,
  } = SetActions();

  const geocoder = new MapboxGeocoder({
    accessToken: process.env.REACT_APP_MAPBOX_TOKEN as string,
    localGeocoder: coordinatesGeocoder as unknown as (
      query: string
    ) => Result[],
    zoom: 4,
    placeholder: t(`Create_site.stepone.search`),
    mapboxgl: mapboxgl as any,
    reverseGeocode: true,
  });

  React.useEffect(() => {
    geocoder.addTo("#geocoder");
    const results = document.getElementById("result") as any;
    let searchResult = results?.innerText as void | FluxStandardAction<
      string,
      any,
      undefined
    >;

    geocoder.on("result", (e: any) => {
      searchResult =
        e.result.geometry.coordinates[1] < -90 ||
        e.result.geometry.coordinates[1] > 90
          ? console.log(e)
          : setSearchAddressEditLoc(
              e.result.geometry.coordinates[0],
              e.result.geometry.coordinates[1]
            );

      geocoder.on("clear", () => {
        selectEditLocationValues({
          ...selectedLocation,
          values: {
            ...selectedLocation.values,
            address: selectedLocation.values.address,
            longitude: selectedLocation.values.longitude,
            latitude: selectedLocation.values.latitude,
          },
        });
      });
    });
  }, []);

  const encounterErrorLatitude = isLatitudeVerify(parseFloat(latitude as any));
  const encounterErrorLongitude = isLongitudeVerify(
    parseFloat(longitude as any)
  );

  const showingLocationExistError = () => {
    if (!encounterErrorLatitude) {
      return (
        <Alert severity="info">
          {t("Create_multi_location.step_two.latitude_validation")}
        </Alert>
      );
    }
    if (!encounterErrorLongitude) {
      return (
        <Alert severity="info">
          {t("Create_multi_location.step_two.longitude_validation")}
        </Alert>
      );
    }
    if (!encounterErrorLatitude && !encounterErrorLongitude) {
      return (
        <Alert severity="info">
          {`${t("Create_multi_location.step_two.longitude_validation")}, """${t(
            "Create_multi_location.step_two.latitude_validation"
          )} `}
        </Alert>
      );
    } else {
      return "";
    }
  };

  if (locationSelectedIs.EditingValues) {
    const managingGTEdition = () => {
      if (locationSelectedIs.EditingStockage) {
        setEditionValues({
          ...locationSelectedIs,
          EditingStockage: false,
        });
      } else {
        setEditionValues({
          ...locationSelectedIs,
          EditingStockage: true,
        });
      }
    };

    return (
      <div className="steps-container-create-loc">
        <div className="step-title">{t("Create_site.stepone.step")}</div>
        <div>
          <div className="step-title-create">
            {t("Edit_location.sub-title")}
          </div>
          <div>{showingLocationExistError()}</div>
          <div className="create-site-form" style={{ background: "#fff" }}>
            <Box
              component="form"
              sx={{ "& > :not(style)": { m: 1 } }}
              noValidate
              autoComplete="off"
            >
              <div className="input-create-loc-form">
                <TextField
                  inputProps={{ style: { height: "31.5px" } }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label={t("Create_site.stepone.site_name")}
                  value={name}
                  variant="outlined"
                  onChange={(e) =>
                    selectEditLocationValues({
                      ...selectedLocation,
                      values: {
                        ...selectedLocation.values,
                        name: e.target.value,
                      },
                    })
                  }
                />
              </div>

              <div className="container-address-form">
                <div className="container-address-input">
                  <label className="form-label-input-address">
                    {t("Create_site.stepone.site_address")}
                  </label>
                  <div id="geocoder" />
                </div>
                <span className="or-span-create-loc">
                  {t("Create_site.stepone.or")}
                </span>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  inputProps={{ style: { height: "31.5px" } }}
                  error={!!selectedLocation.error}
                  sx={{
                    marginTop: "-10.5px",
                    width: 150,
                    maxWidth: "calc(100% - 20px)",
                  }}
                  onChange={(e) =>
                    selectEditLocationValues({
                      ...selectedLocation,
                      values: {
                        ...selectedLocation.values,
                        latitude: e.target.value as unknown as number,
                      },
                    })
                  }
                  value={latitude}
                  label={t("Create_site.stepone.latitude")}
                  variant="outlined"
                />
                -
                <TextField
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    marginTop: "-10.5px",
                    width: 150,
                    maxWidth: "calc(100% - 20px)",
                    marginRight: "24px",
                  }}
                  onChange={(e) =>
                    selectEditLocationValues({
                      ...selectedLocation,
                      values: {
                        ...selectedLocation.values,
                        longitude: e.target.value as unknown as number,
                      },
                    })
                  }
                  inputProps={{
                    style: { height: "31.5px" },
                    inputMode: "numeric",
                  }}
                  error={!!selectedLocation.error}
                  value={longitude}
                  label={t("Create_site.stepone.longitude")}
                  variant="outlined"
                  type="number"
                />
                {selectedLocation.error ? (
                  <div className="error-find-location-create-location">
                    {selectedLocation.error}
                  </div>
                ) : (
                  ""
                )}
              </div>
              <div className="current-location-edit-location">
                {`${t("Edit_location.actual_address")} : `}
                {selectedLocation?.values?.address}
              </div>
              <div className="create-site-form-2"></div>
            </Box>
          </div>
          <div className="create-site-form mt-3" style={{ background: "#fff" }}>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    onChange={() => managingGTEdition()}
                    checked={locationSelectedIs.EditingStockage}
                  />
                }
                label={t("Grain_tracker.activate_graintracker")}
              />
            </div>

            <div className="graintracker-desc-switch">
              {t("Grain_tracker.switch_desc")}
            </div>
          </div>
          <div>
            <Button
              variant="contained"
              sx={{ textTransform: "none", marginTop: "24px" }}
              disabled={
                !name ||
                !longitude ||
                !latitude ||
                !locationSelectedIs.EditingStockage
              }
              onClick={() => {
                updateLocation(token, navigate);
              }}
            >
              {t("Create_site.stepone.Continue")}
            </Button>

            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                marginTop: "24px",
                marginLeft: "10px",
              }}
              disabled={
                (name === locationSelectedDetails?.name &&
                  longitude === locationSelectedDetails.longitude &&
                  latitude === locationSelectedDetails.latitude) ||
                !encounterErrorLatitude ||
                !encounterErrorLongitude ||
                locationSelectedIs.EditingStockage
              }
              onClick={() => {
                updateLocation(token, navigate);
              }}
            >
              {t("Edit_location.save_and_quite")}
            </Button>
          </div>
        </div>
        <div className="background-components-fix" />
      </div>
    );
  } else {
    navigate("/locations");
  }
};

export default EditLocationForm;
